import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../utils/handleError';
import { ContingentApi } from '../../_helpers/service';
import i18next from 'i18next';
import { MenuApi } from './MenuApi';
import Notice from '../../utils/Notice';

/**
 * Constants
 */

export const menuModule = 'menu';
const LOADING_TABLE = `${menuModule}/LOADING_TABLE`;
const TABLE_DATA = `${menuModule}/TABLE_DATA`;
const RECENT_TABLE_DATA = `${menuModule}/RECENT_TABLE_DATA`;
const CLEAR_STATE = `${menuModule}/CLEAR_STATE`;
const SET_ORGANIZATIONS = `${menuModule}/SET_ORGANIZATIONS`;
const SET_AGE_GROUPS = `${menuModule}/SET_AGE_GROUPS`;

/**
 * Actions
 */

const initialState = {
  loadingTable: false,
  tableData: {
    content: [],
    totalElements: 0
  },
  recentMenuTableData: { content: [], totalElements: 0 },
  organizations: [],
  ageGroups: []
};

export default createReducer(initialState, {
  [LOADING_TABLE]: (state, action) => {
    state.loadingTable = action.payload;
  },
  [TABLE_DATA]: (state, action) => {
    state.tableData = action.payload;
  },
  [RECENT_TABLE_DATA]: (state, action) => {
    state.recentMenuTableData = action.payload;
  },
  [SET_ORGANIZATIONS]: (state, action) => {
    state.organizations = action.payload.map(item => {
      return { ...item, code: item.id };
    });
  },
  [SET_AGE_GROUPS]: (state, action) => {
    state.ageGroups = action.payload.map(item => {
      return { ...item, code: item.id, kk_name: item.nameGroup, ru_name: item.nameGroup };
    });
  },
  [CLEAR_STATE]: () => initialState
});

/**

 * Actions

 */

export const clearState = () => ({ type: CLEAR_STATE });

export const loadOrganizations = locationCode => async dispatch => {
  try {
    let { data } = await ContingentApi.getOrganizations(locationCode);
    if (data.status === 'SUCCESS') {
      dispatch({ type: SET_ORGANIZATIONS, payload: data.result || [] });
    } else {
      handleError(data, i18next.t('menuPage_errorLoadOrganizations'));
    }
  } catch (error) {
    handleError(error, i18next.t('menuPage_errorLoadOrganizations'));
  }
};
// ---------------------------------------------

export const loadMenuList = filter => async (dispatch, getState) => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const user = getState().login.user;
    const roles = user?.roles || [];
    const providerRoles = ['ES_SERVICE_PROVIDER_HEADS', 'ES_SERVICE_PROVIDER_EMPLOYEES'];
    const isProvider = roles.some(r => providerRoles.includes(r));
    if (isProvider) {
      if (!filter.serviceProviderId) {
        filter.serviceProviderId = user.organization_id;
      }
    }
    if (isProvider && !filter.serviceProviderId) {
      filter.serviceProviderId = user.organization_id;
    }
    let { data } = await MenuApi.loadMenuList(JSON.stringify(filter));
    if (data.status === 'SUCCESS') {
      const result = data.result || {};
      dispatch({
        type: TABLE_DATA,
        payload: {
          content: result.content || [],
          totalElements: result.totalElements || 0,
          pageNumber: result.pageNumber || 1,
          pageSize: result.pageSize || 20,
          totalPages: result.totalPages || 1
        },
        filter
      });
    } else {
      handleError(data, i18next.t('menuPage_errorLoadMenu'));
    }
  } catch (error) {
    handleError(error, i18next.t('menuPage_errorLoadMenu'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const allGroupsMenu = serviceProviderId => async dispatch => {
  try {
    const { data } = await MenuApi.loadAgeGroups(serviceProviderId);
    if (data.status === 'SUCCESS') {
      dispatch({ type: SET_AGE_GROUPS, payload: data.result });
    } else {
      handleError(data, i18next.t('errorLoadGroups'));
    }
  } catch (error) {
    handleError(error, i18next.t('errorLoadGroups'));
  }
};

export const addMenu = (params, onSuccessClose) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });
    const { data } = await MenuApi.addMenu(params);
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('menuPage_successAdd'));
      if (onSuccessClose) onSuccessClose();
    }
  } catch (error) {
    const code = error?.response?.data?.errors?.[0]?.code;
    if (code === 'error.menu_period_expired') {
      Notice.error(i18next.t('menuPage_errorExpiredDate'));
    }
    else if (code === 'error.menu_overlap_error') {
      Notice.error(i18next.t('menuPage_errorOverlapsMenu'));
    }
    else {
      handleError(error, i18next.t('menuPage_errorAddMenu'));
    }
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const updateMenu = (params, onSuccessClose) => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const { data } = await MenuApi.updateMenu(params);
    if (data.status === 'SUCCESS') {
      Notice.success(i18next.t('menuPage_successEdit'));
      if (onSuccessClose) onSuccessClose();
    }
  } catch (error) {
    const code = error?.response?.data?.errors?.[0]?.code;
    if (code === 'error.menu_period_expired') {
      Notice.error(i18next.t('menuPage_errorExpiredDate'));
    }
    else if (code === 'error.menu_overlap_error') {
      Notice.error(i18next.t('menuPage_errorOverlapsMenu'));
    }
    else if(code === 'error.menu_already_active') {
      Notice.error(i18next.t('menuPage_errorActiveMenu'));
    }
    else {
      handleError(error, i18next.t('menuPage_errorEditMenu'));
    }
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const loadRecentMenuList = filter => async (dispatch, getState) => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });

    const user = getState().login.user;
    const roles = user?.roles || [];
    const providerRoles = ['ES_SERVICE_PROVIDER_HEADS', 'ES_SERVICE_PROVIDER_EMPLOYEES'];
    const isProvider = roles.some(r => providerRoles.includes(r));
    if (isProvider) {
      if (!filter.serviceProviderId) {
        filter.serviceProviderId = user.organization_id;
      }
    }
    if (filter.serviceProviderId) {
      dispatch(allGroupsMenu(filter.serviceProviderId));
    }
    let { data } = await MenuApi.loadMenuList(JSON.stringify(filter));
    if (data.status === 'SUCCESS') {
      const result = data.result || {};
      dispatch({
        type: RECENT_TABLE_DATA,
        payload: {
          content: result.content || [],
          totalElements: result.totalElements || 0,
          pageNumber: result.pageNumber || 1,
          pageSize: result.pageSize || 20,
          totalPages: result.totalPages || 1
        },
        filter
      });
    } else {
      handleError(data, i18next.t('archiveDucks_errorLoadTable'));
    }
  } catch (error) {
    handleError(error, i18next.t('archiveDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};
